import ChevronIcon from '@otovo/shared/components/Icons/ChevronIcon/ChevronIcon';
import { Sanity$TestimonialCards } from '@otovo/shared/types/sanityTypes';
import { PortableText } from '@portabletext/react';
import { useEffect, useRef, useState } from 'react';
import PortableTextComponents from '../../Sanity/PortableTextComponents';

type Props = {
  content: Sanity$TestimonialCards;
};

const TestimonialCards = ({ content }: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1); // -1 for rounding
    }
  };

  useEffect(() => {
    checkScroll();
  }, []);

  const scrollX = 24 * 16 /* w-96 */ + 1.5 * 16; /* gap-6 */

  return (
    <div
      className="color-scheme-white bg-surface"
      data-testid="testimonial-cards"
    >
      <div className="mx-auto max-w-8xl px-4 py-16 md:px-6 md:py-20 lg:px-16 lg:py-24">
        <div className="grid-cols-12 gap-4 md:grid md:gap-6">
          <div className="col-span-9 mb-8 space-y-3.5 md:col-span-4 md:mb-0">
            <h2 className="text-2xl font-semibold text-balance text-heading md:text-3xl lg:text-4xl">
              {content.heading}
            </h2>
          </div>
          <div className="color-scheme-dark relative col-span-12 lg:col-span-7 lg:col-start-6 xl:col-span-8 xl:col-start-5">
            <div
              className="flex snap-x gap-6 overflow-x-auto"
              style={{
                scrollbarWidth: 'none',
              }}
              ref={scrollRef}
              onScroll={checkScroll}
            >
              {content.testimonials.map((testimonial) => (
                <figure
                  key={testimonial._key}
                  className="w-96 shrink-0 snap-start rounded-xl bg-surface p-5 lg:p-8"
                >
                  <blockquote className="mb-12 block text-body italic">
                    <PortableText
                      value={testimonial.quote}
                      components={PortableTextComponents}
                    />
                  </blockquote>
                  <figcaption>
                    <cite className="block font-semibold text-body not-italic">
                      {testimonial.customerName}
                    </cite>
                    <span className="mb-3 block text-sm text-strong">
                      {testimonial.location}
                    </span>
                    <span className="inline-block rounded-lg bg-green-60 px-3 py-1 text-xs text-strong">
                      {testimonial.tag}
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
            {/* Left scroll indicator */}
            {canScrollLeft && (
              <button
                type="button"
                onClick={() => {
                  if (scrollRef.current) {
                    scrollRef.current.scrollBy({
                      left: -scrollX,
                      behavior: 'smooth',
                    });
                  }
                }}
                className="absolute top-1/2 left-2 -translate-y-1/2 rounded-full bg-body/70 p-2 shadow-lg hover:bg-strong/90"
              >
                <span className="sr-only">
                  {content.ariaLabelScrollLeft || 'Scroll left'}
                </span>
                <ChevronIcon className="h-5 w-5 rotate-180 text-grey-60" />
              </button>
            )}

            {/* Right scroll indicator */}
            {canScrollRight && (
              <button
                type="button"
                onClick={() => {
                  if (scrollRef.current) {
                    scrollRef.current.scrollBy({
                      left: scrollX,
                      behavior: 'smooth',
                    });
                  }
                }}
                className="absolute top-1/2 right-2 -translate-y-1/2 rounded-full bg-body/70 p-2 shadow-lg hover:bg-strong/90"
              >
                <span className="sr-only">
                  {content.ariaLabelScrollRight || 'Scroll right'}
                </span>
                <ChevronIcon className="h-5 w-5 text-grey-60" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestimonialCards;
